import React from 'react';
import { Helmet } from 'react-helmet';
import { Shield, Lock, Eye, Database, Mail, Clock, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <Helmet>
        <title>Privacy Policy | ActionFraudOne</title>
        <meta name="description" content="ActionFraudOne Privacy Policy. Learn how we collect, use, and protect your personal data in accordance with UK GDPR." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
              <Lock className="w-4 h-4 text-gold" />
              <span className="text-gold text-sm font-medium">Data Protection</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400">Last updated: March 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Introduction */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    ActionFraudOne ("we", "our", "us") is committed to protecting and respecting your privacy. 
                    This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our services.
                  </p>
                  <p>
                    We operate in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data We Collect */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-gold" />
                  2. Information We Collect
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>We collect and process the following types of personal data:</p>
                  
                  <div className="space-y-4 mt-4">
                    <div className="p-4 bg-black/50 rounded-lg border border-gold/10">
                      <h3 className="text-white font-semibold mb-2">Contact Information</h3>
                      <ul className="space-y-1 text-sm">
                        <li>• Full name</li>
                        <li>• Email address</li>
                        <li>• Phone number (if provided)</li>
                        <li>• Postal address (if provided)</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-black/50 rounded-lg border border-gold/10">
                      <h3 className="text-white font-semibold mb-2">Service-Related Data</h3>
                      <ul className="space-y-1 text-sm">
                        <li>• Uploaded screenshots or messages for scam analysis</li>
                        <li>• Descriptions of suspected fraudulent activity</li>
                        <li>• Website URLs or phone numbers being investigated</li>
                        <li>• Communication records related to your case</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-black/50 rounded-lg border border-gold/10">
                      <h3 className="text-white font-semibold mb-2">Payment Information</h3>
                      <ul className="space-y-1 text-sm">
                        <li>• Transaction records (processed securely via Stripe)</li>
                        <li>• We do not store full payment card details</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Data */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gold" />
                  3. How We Use Your Information
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>We use your personal data for the following purposes:</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Fraud Analysis:</strong> To analyse submitted materials and identify scam patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Communication:</strong> To contact you regarding your case and deliver reports</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Service Delivery:</strong> To process payments and provide the services you have purchased</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Legal Compliance:</strong> To comply with legal obligations where required</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gold" />
                  4. Data Protection & Security
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    We implement appropriate technical and organisational measures to protect your personal data against 
                    unauthorised access, alteration, disclosure, or destruction.
                  </p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Encrypted data transmission (SSL/TLS)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Secure data storage with access controls</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Regular security assessments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>Staff confidentiality agreements</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gold" />
                  5. Data Retention
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, 
                    or as required by law. Typically:
                  </p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Case files and reports: Retained for 7 years for legal compliance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Contact enquiries: Retained for 2 years</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span>Marketing communications: Until you unsubscribe</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">6. Your Rights</h2>
                <div className="text-gray-400 space-y-4">
                  <p>Under UK GDPR, you have the following rights:</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Right of Access:</strong> Request a copy of your personal data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Right to Rectification:</strong> Request correction of inaccurate data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Right to Erasure:</strong> Request deletion of your data (subject to legal obligations)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Right to Restrict Processing:</strong> Request limitation of how we use your data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong className="text-white">Right to Data Portability:</strong> Receive your data in a structured format</span>
                    </li>
                  </ul>
                  <p className="mt-4">
                    To exercise any of these rights, please contact us at{' '}
                    <a href="mailto:info@actionfraudone.com" className="text-gold hover:underline">info@actionfraudone.com</a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-gold" />
                  7. Contact Us
                </h2>
                <div className="text-gray-400 space-y-4">
                  <p>
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="p-4 bg-black/50 rounded-lg border border-gold/10 mt-4">
                    <p className="text-white font-semibold">ActionFraudOne</p>
                    <p>167-169 Great Portland Street, 5th Floor</p>
                    <p>London, W1W 5PF</p>
                    <p className="mt-2">
                      Email: <a href="mailto:info@actionfraudone.com" className="text-gold hover:underline">info@actionfraudone.com</a>
                    </p>
                    <p>Phone: +44 7386 668966</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ICO Notice */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400 text-sm">
                If you are not satisfied with our response or believe we are processing your data unlawfully, 
                you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">ico.org.uk</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
